import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './CodeKeyboxPage.css';

const BASE_CODE = '04061210';
const WEBHOOK_URL = 'https://n8n.atoneo.com/webhook/commentaire-site';
const SUPPORT_PHONE = '+39 376 282 4580';
const SUPPORTED_LANGS = ['en', 'fr', 'it', 'de', 'es', 'pt', 'ru'];
const LANG_FLAGS = { en: '🇬🇧', fr: '🇫🇷', it: '🇮🇹', de: '🇩🇪', es: '🇪🇸', pt: '🇵🇹', ru: '🇷🇺' };
const DEBUG_PREFIX = '[KeyboxDebug]';
const REQUEST_TYPES = {
  ENTRY: 'entry',
  EXIT: 'exit',
};

const COPY = {
  en: {
    title: 'Keybox code',
    subtitle: 'Enter your details to retrieve your keybox code.',
    requestType: 'Request type',
    requestTypeEntry: 'Arrival code',
    requestTypeExit: 'Exit code',
    firstName: 'First name',
    firstNamePlaceholder: 'Ex: Luca',
    phone: 'Phone number',
    phonePlaceholder: '+33123456789',
    arrivalDate: 'Arrival date',
    exitDate: 'Exit date',
    submit: 'Show code',
    loading: 'Checking…',
    codeLabel: 'Code',
    errorMissingFields: 'Please enter first name, phone number, and arrival date.',
    errorFirstNameTooShort: 'First name must contain at least 3 letters.',
    errorInvalidPhone: 'Invalid phone format. Use international format, e.g. +33123456789.',
    errorInvalidDate: 'Invalid arrival date.',
    errorNoMatch: 'No matching booking found. Please check first name and date.',
    errorGeneric: 'An error occurred.',
    codeValidityNote: 'This code is valid only for today. You must generate the exit code on the departure date.',
    language: 'Language',
    helpContact: `If you have any problem, contact me at: ${SUPPORT_PHONE}`,
  },
  fr: {
    title: 'Code de la keybox',
    subtitle: 'Entrez vos informations pour obtenir le code de la keybox.',
    requestType: 'Type de demande',
    requestTypeEntry: 'Code d\'arrivée',
    requestTypeExit: 'Code de sortie',
    firstName: 'Prénom',
    firstNamePlaceholder: 'Ex: Luca',
    phone: 'Téléphone',
    phonePlaceholder: '+33612345678',
    arrivalDate: 'Date d\'arrivée',
    exitDate: 'Date de sortie',
    submit: 'Voir le code',
    loading: 'Vérification…',
    codeLabel: 'Code',
    errorMissingFields: 'Merci d\'indiquer le prénom, le téléphone et la date d\'arrivée.',
    errorFirstNameTooShort: 'Le prénom doit contenir au moins 3 lettres.',
    errorInvalidPhone: 'Format de téléphone invalide. Utilisez le format international, ex: +33612345678.',
    errorInvalidDate: 'Date d\'arrivée invalide.',
    errorNoMatch: 'Aucune réservation correspondante trouvée. Vérifiez le prénom et la date.',
    errorGeneric: 'Une erreur est survenue.',
    codeValidityNote: 'Ce code est valable uniquement pour ce jour. Il faudra générer le code de sortie à la date de la sortie.',
    language: 'Langue',
    helpContact: `Si vous rencontrez un probleme contacter moi au : ${SUPPORT_PHONE}`,
  },
  it: {
    title: 'Codice keybox',
    subtitle: 'Inserisci i tuoi dati per ottenere il codice della keybox.',
    requestType: 'Tipo richiesta',
    requestTypeEntry: 'Codice arrivo',
    requestTypeExit: 'Codice uscita',
    firstName: 'Nome',
    firstNamePlaceholder: 'Es: Luca',
    phone: 'Telefono',
    phonePlaceholder: '+393331234567',
    arrivalDate: 'Data di arrivo',
    exitDate: 'Data di uscita',
    submit: 'Mostra codice',
    loading: 'Verifica…',
    codeLabel: 'Codice',
    errorMissingFields: 'Inserisci nome, telefono e data di arrivo.',
    errorFirstNameTooShort: 'Il nome deve contenere almeno 3 lettere.',
    errorInvalidPhone: 'Formato telefono non valido. Usa il formato internazionale, es: +393331234567.',
    errorInvalidDate: 'Data di arrivo non valida.',
    errorNoMatch: 'Nessuna prenotazione corrispondente trovata. Verifica nome e data.',
    errorGeneric: 'Si è verificato un errore.',
    codeValidityNote: 'Questo codice è valido solo per oggi. Dovrai generare il codice di uscita alla data di partenza.',
    language: 'Lingua',
    helpContact: `Se riscontri un problema contattami al: ${SUPPORT_PHONE}`,
  },
  de: {
    title: 'Schlüsselbox-Code',
    subtitle: 'Geben Sie Ihre Daten ein, um den Keybox-Code zu erhalten.',
    requestType: 'Anfragetyp',
    requestTypeEntry: 'Anreise-Code',
    requestTypeExit: 'Abreise-Code',
    firstName: 'Vorname',
    firstNamePlaceholder: 'Z. B. Luca',
    phone: 'Telefon',
    phonePlaceholder: '+4915123456789',
    arrivalDate: 'Anreisedatum',
    exitDate: 'Abreisedatum',
    submit: 'Code anzeigen',
    loading: 'Prüfung…',
    codeLabel: 'Code',
    errorMissingFields: 'Bitte Vorname, Telefon und Anreisedatum eingeben.',
    errorFirstNameTooShort: 'Der Vorname muss mindestens 3 Buchstaben enthalten.',
    errorInvalidPhone: 'Ungültiges Telefonformat. Bitte internationales Format verwenden, z. B. +4915123456789.',
    errorInvalidDate: 'Ungültiges Anreisedatum.',
    errorNoMatch: 'Keine passende Buchung gefunden. Bitte Vorname und Datum prüfen.',
    errorGeneric: 'Ein Fehler ist aufgetreten.',
    codeValidityNote: 'Dieser Code ist nur heute gültig. Der Abreise-Code muss am Abreisetag erzeugt werden.',
    language: 'Sprache',
    helpContact: `Wenn Sie ein Problem haben, kontaktieren Sie mich unter: ${SUPPORT_PHONE}`,
  },
  es: {
    title: 'Código de la keybox',
    subtitle: 'Introduce tus datos para obtener el código de la keybox.',
    requestType: 'Tipo de solicitud',
    requestTypeEntry: 'Código de llegada',
    requestTypeExit: 'Código de salida',
    firstName: 'Nombre',
    firstNamePlaceholder: 'Ej: Luca',
    phone: 'Teléfono',
    phonePlaceholder: '+34612345678',
    arrivalDate: 'Fecha de llegada',
    exitDate: 'Fecha de salida',
    submit: 'Ver código',
    loading: 'Comprobando…',
    codeLabel: 'Código',
    errorMissingFields: 'Introduce nombre, teléfono y fecha de llegada.',
    errorFirstNameTooShort: 'El nombre debe contener al menos 3 letras.',
    errorInvalidPhone: 'Formato de teléfono no válido. Usa formato internacional, ej: +34612345678.',
    errorInvalidDate: 'Fecha de llegada no válida.',
    errorNoMatch: 'No se encontró una reserva correspondiente. Verifica nombre y fecha.',
    errorGeneric: 'Ha ocurrido un error.',
    codeValidityNote: 'Este código es válido solo para hoy. Deberás generar el código de salida en la fecha de salida.',
    language: 'Idioma',
    helpContact: `Si tienes algún problema, contáctame en: ${SUPPORT_PHONE}`,
  },
  pt: {
    title: 'Codigo da keybox',
    subtitle: 'Insira os seus dados para obter o código da keybox.',
    requestType: 'Tipo de pedido',
    requestTypeEntry: 'Código de chegada',
    requestTypeExit: 'Código de saída',
    firstName: 'Primeiro nome',
    firstNamePlaceholder: 'Ex: Luca',
    phone: 'Telefone',
    phonePlaceholder: '+351912345678',
    arrivalDate: 'Data de chegada',
    exitDate: 'Data de saída',
    submit: 'Ver código',
    loading: 'A verificar…',
    codeLabel: 'Código',
    errorMissingFields: 'Informe o primeiro nome, telefone e a data de chegada.',
    errorFirstNameTooShort: 'O primeiro nome deve ter pelo menos 3 letras.',
    errorInvalidPhone: 'Formato de telefone inválido. Use formato internacional, ex: +351912345678.',
    errorInvalidDate: 'Data de chegada inválida.',
    errorNoMatch: 'Nenhuma reserva correspondente encontrada. Verifique nome e data.',
    errorGeneric: 'Ocorreu um erro.',
    codeValidityNote: 'Este código é válido apenas para hoje. Terá de gerar o código de saída na data de saída.',
    language: 'Idioma',
    helpContact: `Se tiver algum problema, contacte-me em: ${SUPPORT_PHONE}`,
  },
  ru: {
    title: 'Kod keybox',
    subtitle: 'Vvedite dannye, chtoby poluchit kod keybox.',
    requestType: 'Tip zaprosa',
    requestTypeEntry: 'Kod zaezda',
    requestTypeExit: 'Kod vyezda',
    firstName: 'Imya',
    firstNamePlaceholder: 'Naprimer: Luca',
    phone: 'Telefon',
    phonePlaceholder: '+79991234567',
    arrivalDate: 'Data zaezda',
    exitDate: 'Data vyezda',
    submit: 'Pokazat kod',
    loading: 'Proverka…',
    codeLabel: 'Kod',
    errorMissingFields: 'Ukazhite imya, telefon i datu zaezda.',
    errorFirstNameTooShort: 'Imya dolzhno soderzhat minimum 3 bukvy.',
    errorInvalidPhone: 'Nevernyy format telefona. Ispolzuyte mezhdunarodnyy format, naprimer +79991234567.',
    errorInvalidDate: 'Nevernaya data zaezda.',
    errorNoMatch: 'Podhodyashchee bronirovanie ne naydeno. Proverte imya i datu.',
    errorGeneric: 'Proizoshla oshibka.',
    codeValidityNote: 'Etot kod deystvuet tolko segodnya. Kod vyezda nuzhno sgenirovat v den vyezda.',
    language: 'Yazyk',
    helpContact: `Esli u vas est problema, svyazhites so mnoy: ${SUPPORT_PHONE}`,
  },
};

const normalizeText = (value) => {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
};

const weekdayFromArrival = (ymd) => {
  const date = new Date(`${ymd}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  // JS: dimanche=0 ... samedi=6, on convertit en lundi=1 ... dimanche=7.
  return ((date.getDay() + 6) % 7) + 1;
};

const hasMatchingFirstName = (reservationName, requestedFirstName) => {
  const normalizedNeedle = normalizeText(requestedFirstName);
  if (!normalizedNeedle) {
    return false;
  }

  const haystack = normalizeText(reservationName);
  return haystack.includes(normalizedNeedle);
};

const reservationStartFromKey = (reservationKey) => {
  if (typeof reservationKey !== 'string') {
    return '';
  }

  const [start] = reservationKey.split('_');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start || '')) {
    return '';
  }

  return start;
};

const reservationEndFromKey = (reservationKey) => {
  if (typeof reservationKey !== 'string') {
    return '';
  }

  const [, end] = reservationKey.split('_');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(end || '')) {
    return '';
  }

  return end;
};

const addDaysToYmd = (ymd, days) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd || '')) {
    return '';
  }

  const dt = new Date(`${ymd}T00:00:00`);
  if (Number.isNaN(dt.getTime())) {
    return '';
  }

  dt.setDate(dt.getDate() + days);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const isArrivalMatchingStart = (startDate, arrivalDate) => {
  if (!startDate || !arrivalDate) {
    return { strict: false, plusOne: false, match: false };
  }

  const strict = startDate === arrivalDate;
  const plusOne = addDaysToYmd(startDate, 1) === arrivalDate;

  return { strict, plusOne, match: strict || plusOne };
};

const isExitMatchingEnd = (endDate, exitDate) => {
  if (!endDate || !exitDate) {
    return { strict: false, plusOne: false, match: false };
  }

  const strict = endDate === exitDate;
  const plusOne = addDaysToYmd(endDate, 1) === exitDate;

  return { strict, plusOne, match: strict || plusOne };
};

const isValidInternationalPhone = (value) => {
  const phone = String(value || '').trim();
  return /^\+[1-9]\d{7,14}$/.test(phone);
};

const firstNameLetterCount = (value) => {
  const normalized = normalizeText(value);
  const letters = normalized.match(/[a-z]/g);
  return letters ? letters.length : 0;
};

const CodeKeyboxPage = () => {
  const navigate = useNavigate();
  const { lang } = useParams();
  const activeLang = SUPPORTED_LANGS.includes(lang) ? lang : 'en';
  const t = COPY[activeLang] || COPY.en;

  const [firstName, setFirstName] = useState('');
  const [phone, setPhone] = useState('');
  const [requestType, setRequestType] = useState(REQUEST_TYPES.ENTRY);
  const [travelDate, setTravelDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [keyboxCode, setKeyboxCode] = useState('');

  useEffect(() => {
    if (lang && !SUPPORTED_LANGS.includes(lang)) {
      navigate('/code/en', { replace: true });
    }
  }, [lang, navigate]);

  const notifyWebhook = async ({ code, result }) => {
    const resultLabel = {
      match_found: 'match found',
      no_match: 'no match',
      missing_fields: 'missing fields',
      invalid_phone: 'invalid phone',
      invalid_date: 'invalid date',
      invalid_first_name_too_short: 'first name too short',
      error: 'error',
    }[result] || result;

    const msgLines = [
      'Keybox code request',
      '',
      `Langue: ${activeLang}`,
      `Type demande: ${requestType === REQUEST_TYPES.EXIT ? 'exit' : 'entry'}`,
      `Prénom saisi: ${firstName.trim() || '—'}`,
      `Téléphone saisi: ${phone.trim() || '—'}`,
      `Date demandée: ${travelDate || '—'}`,
      `Résultat: ${resultLabel}`,
      `Code fourni: ${code || '—'}`,
    ];

    const payload = { type: 'keybox_request', message: msgLines.join('\n') };

    // Logs explicites pour faciliter le debug côté navigateur.
    console.log(`${DEBUG_PREFIX} webhook.request`, {
      proxyUrl: WEBHOOK_URL,
      result,
      code: code || null,
      firstName: firstName.trim(),
      phone: phone.trim(),
      requestType,
      travelDate,
      payload,
    });

    try {
      // Même format d'appel que le bouton "📤 Invia riepilogo".
      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body: JSON.stringify(payload),
      });

      let responsePayload = null;
      try {
        responsePayload = await response.json();
      } catch (_) {
        responsePayload = null;
      }

      console.log(`${DEBUG_PREFIX} webhook.response`, {
        ok: response.ok,
        status: response.status,
        responsePayload,
      });
    } catch (_) {
      console.error(`${DEBUG_PREFIX} webhook.error`, _);
      // Ne bloque jamais le formulaire si le webhook est indisponible.
    }
  };

  const canSubmit = useMemo(() => {
    return firstName.trim().length > 0 && phone.trim().length > 0 && travelDate.trim().length > 0 && !isLoading;
  }, [firstName, isLoading, phone, travelDate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setKeyboxCode('');

    console.log(`${DEBUG_PREFIX} submit.start`, {
      lang: activeLang,
      requestType,
      firstNameRaw: firstName,
      firstNameNormalized: normalizeText(firstName),
      phoneRaw: phone,
      travelDate,
    });

    if (!firstName.trim() || !phone.trim() || !travelDate.trim()) {
      setError(t.errorMissingFields);
      await notifyWebhook({ code: '', result: 'missing_fields' });
      console.warn(`${DEBUG_PREFIX} submit.stop missing_fields`);
      return;
    }

    if (firstNameLetterCount(firstName) < 3) {
      setError(t.errorFirstNameTooShort);
      await notifyWebhook({ code: '', result: 'invalid_first_name_too_short' });
      console.warn(`${DEBUG_PREFIX} submit.stop invalid_first_name_too_short`);
      return;
    }

    if (!isValidInternationalPhone(phone)) {
      setError(t.errorInvalidPhone);
      await notifyWebhook({ code: '', result: 'invalid_phone' });
      console.warn(`${DEBUG_PREFIX} submit.stop invalid_phone`);
      return;
    }

    const weekday = weekdayFromArrival(travelDate);
    if (!weekday) {
      setError(t.errorInvalidDate);
      await notifyWebhook({ code: '', result: 'invalid_date' });
      console.warn(`${DEBUG_PREFIX} submit.stop invalid_date`);
      return;
    }

    console.log(`${DEBUG_PREFIX} weekday.computed`, { weekday });

    setIsLoading(true);

    try {
      const response = await fetch('/api/bookings/overlays.php', { cache: 'no-store' });
      const payload = await response.json();

      if (!response.ok || !payload?.success) {
        throw new Error(payload?.error || 'Impossible de vérifier la réservation.');
      }

      const overlays = payload?.data?.overlays;
      const entries = overlays && typeof overlays === 'object' ? Object.entries(overlays) : [];

      console.log(`${DEBUG_PREFIX} overlays.fetched`, {
        overlaysType: typeof overlays,
        overlaysCount: entries.length,
      });

      const debugRows = entries.map(([reservationKey, overlay]) => {
        const startDate = reservationStartFromKey(reservationKey);
        const endDate = reservationEndFromKey(reservationKey);
        const dateChecks =
          requestType === REQUEST_TYPES.EXIT
            ? isExitMatchingEnd(endDate, travelDate)
            : isArrivalMatchingStart(startDate, travelDate);
        const nameRaw = overlay && typeof overlay === 'object' ? (overlay.name || '') : '';
        const nameMatch = hasMatchingFirstName(nameRaw, firstName);
        const bookingMode = overlay && typeof overlay === 'object' ? overlay.bookingMode : '';

        return {
          reservationKey,
          startDate,
          endDate,
          dateMatchStrict: dateChecks.strict,
          dateMatchPlusOne: dateChecks.plusOne,
          dateMatch: dateChecks.match,
          bookingMode,
          nameRaw,
          nameNormalized: normalizeText(nameRaw),
          requestedFirstName: firstName,
          requestedFirstNameNormalized: normalizeText(firstName),
          nameMatch,
        };
      });

      console.table(debugRows);

      const match = entries.find(([reservationKey, overlay]) => {
        if (!overlay || typeof overlay !== 'object') {
          return false;
        }

        if (overlay.bookingMode === 'errore') {
          return false;
        }

        const startDate = reservationStartFromKey(reservationKey);
        const endDate = reservationEndFromKey(reservationKey);
        const dateChecks =
          requestType === REQUEST_TYPES.EXIT
            ? isExitMatchingEnd(endDate, travelDate)
            : isArrivalMatchingStart(startDate, travelDate);
        if (!dateChecks.match) {
          return false;
        }

        return hasMatchingFirstName(overlay.name, firstName);
      });

      if (!match) {
        setError(t.errorNoMatch);
        await notifyWebhook({ code: '', result: 'no_match' });
        console.warn(`${DEBUG_PREFIX} submit.stop no_match`);
        return;
      }

      console.log(`${DEBUG_PREFIX} match.found`, {
        reservationKey: match[0],
        overlay: match[1],
      });

      const computedCode = `${BASE_CODE}${weekday}`;
      setKeyboxCode(computedCode);
      await notifyWebhook({ code: computedCode, result: 'match_found' });
      console.log(`${DEBUG_PREFIX} submit.success`, { computedCode });
    } catch (err) {
      setError(err?.message || t.errorGeneric);
      await notifyWebhook({ code: '', result: 'error' });
      console.error(`${DEBUG_PREFIX} submit.error`, err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="keybox-page">
      <section className="keybox-card">
        <div className="keybox-logo-container">
          <a href="/" style={{ textDecoration: 'none' }}>
            <img
              src="/images/logo.png"
              alt="Villa Pina e Paolo logo"
              className="keybox-logo"
            />
          </a>
        </div>

        <div className="keybox-languages" aria-label={t.language}>
          {SUPPORTED_LANGS.map((code) => (
            <button
              key={code}
              type="button"
              className={`keybox-lang-btn ${code === activeLang ? 'active' : ''}`}
              onClick={() => navigate(`/code/${code}`)}
              title={code.toUpperCase()}
            >
              {LANG_FLAGS[code] || code.toUpperCase()}
            </button>
          ))}
        </div>

        <h1>{t.title}</h1>
        <p className="keybox-subtitle">{t.subtitle}</p>

        <form className="keybox-form" onSubmit={handleSubmit}>
          <label>
            {t.requestType}
            <select
              value={requestType}
              onChange={(e) => setRequestType(e.target.value === REQUEST_TYPES.EXIT ? REQUEST_TYPES.EXIT : REQUEST_TYPES.ENTRY)}
            >
              <option value={REQUEST_TYPES.ENTRY}>{t.requestTypeEntry}</option>
              <option value={REQUEST_TYPES.EXIT}>{t.requestTypeExit}</option>
            </select>
          </label>

          <label>
            {t.firstName}
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder={t.firstNamePlaceholder}
              autoComplete="given-name"
              minLength={3}
              required
            />
          </label>

          <label>
            {t.phone}
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={t.phonePlaceholder}
              autoComplete="tel"
              inputMode="tel"
              pattern="\+[1-9]\d{7,14}"
              required
            />
          </label>

          <label>
            {requestType === REQUEST_TYPES.EXIT ? t.exitDate : t.arrivalDate}
            <input
              type="date"
              value={travelDate}
              onChange={(e) => setTravelDate(e.target.value)}
              required
            />
          </label>

          <button type="submit" disabled={!canSubmit}>
            {isLoading ? t.loading : t.submit}
          </button>
        </form>

        {error ? <p className="keybox-message keybox-message--error">{error}</p> : null}
        {keyboxCode ? (
          <div className="keybox-message keybox-message--ok">
            <p>
              {t.codeLabel}: <strong>{keyboxCode}</strong>
            </p>
            <p className="keybox-validity-note">{t.codeValidityNote}</p>
          </div>
        ) : null}

        <p className="keybox-contact">{t.helpContact}</p>
      </section>
    </main>
  );
};

export default CodeKeyboxPage;
